import { MOIS_LABELS } from './planDeComWarnings';
import { isRefActiveInMonth } from './kkpStatutUtils';

export const PDC_PRINT_KINDS = ['mag', 'set', 'reportage'];
export const PDC_WEB_KINDS = ['web', 'newsletter'];

/** Points de saisie UI : Mag / Set / Web / Newsletter (reportage regroupé sous Mag). */
export const PDC_SUPPORT_TYPE_GROUPS = [
  { key: 'mag', label: 'Mag', kinds: ['mag', 'reportage'] },
  { key: 'set', label: 'Set', kinds: ['set'] },
  { key: 'web', label: 'Web', kinds: ['web'] },
  { key: 'newsletter', label: 'Newsletter', kinds: ['newsletter'] },
];

export const PDC_SLOT_SEPARATOR = ' || ';

export function planDeComSupportTypeKey(kind) {
  if (kind === 'mag' || kind === 'reportage') return 'mag';
  if (kind === 'set') return 'set';
  if (kind === 'newsletter') return 'newsletter';
  if (kind === 'web') return 'web';
  return 'unknown';
}

export function planDeComMoisToNumber(mois) {
  const key = String(mois ?? '').trim().toUpperCase();
  const idx = MOIS_LABELS.indexOf(key);
  return idx === -1 ? null : idx + 1;
}

export function extractPlanDeComTarifLibelle(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  const separatorPos = raw.lastIndexOf(PDC_SLOT_SEPARATOR);
  const label = separatorPos === -1
    ? raw
    : raw.slice(0, separatorPos).trim();

  const dashIdx = label.indexOf(' — ');
  if (dashIdx === -1) {
    const altDashIdx = label.indexOf(' - ');
    return altDashIdx === -1 ? label.trim() : label.slice(0, altDashIdx).trim();
  }

  return label.slice(0, dashIdx).trim();
}

export function decodePlanDeComSupportValue(value) {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return { raw: '', label: '', slotKey: null, tarifLibelle: '' };
  }

  const separatorPos = raw.lastIndexOf(PDC_SLOT_SEPARATOR);
  if (separatorPos === -1) {
    return {
      raw,
      label: raw,
      slotKey: null,
      tarifLibelle: extractPlanDeComTarifLibelle(raw),
    };
  }

  const label = raw.slice(0, separatorPos).trim();
  const slotKey = raw.slice(separatorPos + PDC_SLOT_SEPARATOR.length).trim();

  return {
    raw,
    label: label || raw,
    slotKey: slotKey || null,
    tarifLibelle: extractPlanDeComTarifLibelle(label || raw),
  };
}

/**
 * Date de diffusion WEB / newsletter (ex. « 05/01/2026 »), ou null.
 */
export function extractPlanDeComSupportDate(value) {
  const decoded = decodePlanDeComSupportValue(value);
  const suffix = planDeComLabelSuffix(decoded.label);
  if (suffix) {
    const fromLabel = normalizePlanDeComDateLabel(suffix);
    if (fromLabel) return fromLabel;
  }

  return dateLabelFromYmd(decoded.slotKey) || dateLabelFromYmd(decoded.raw);
}

function planDeComLabelSuffix(label) {
  const text = String(label ?? '');
  let separator = ' — ';
  let pos = text.indexOf(separator);
  if (pos === -1) {
    separator = ' - ';
    pos = text.indexOf(separator);
  }
  if (pos === -1) return null;
  const suffix = text.slice(pos + separator.length).trim();
  return suffix || null;
}

function normalizePlanDeComDateLabel(value) {
  const match = String(value ?? '').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  return `${String(match[1]).padStart(2, '0')}/${String(match[2]).padStart(2, '0')}/${match[3]}`;
}

function dateLabelFromYmd(value) {
  const match = String(value ?? '').match(/(\d{4}-\d{2}-\d{2})/);
  if (!match) return null;
  const date = new Date(`${match[1]}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
}

/** MAG pages : quantité libre dans le mois. Chaque emplacement SET est un slot distinct. */
export function isPlanDeComSupportRepeatable(value, slotKey = null) {
  const key = slotKey ?? decodePlanDeComSupportValue(value).slotKey;
  if (!key) return false;
  return /^mag:\d+:\d{2}:pub:/.test(String(key));
}

export function classifyPlanDeComSupport(label, categorie = null) {
  const value = extractPlanDeComTarifLibelle(label);
  if (!value) return 'unknown';

  if (/WEB\s+BAN/i.test(value)) return 'web';
  if (/NEWSLETTER|NEWS\s+FOOD|NEWS\s+SORTIES/i.test(value)) return 'newsletter';
  if (categorie === 'produit_web') return 'web';
  if (/REPORTAGE|FEUILLETON/i.test(value)) return 'reportage';
  if (/MAG/i.test(value)) return 'mag';
  // Formats mag sans le mot « MAG » (ex. Monde Food, couvertures, 1/2 page…)
  if (
    /MONDE\s*FOOD/i.test(value)
    || /HOROSCOPE/i.test(value)
    || /(2|3|4)\s*(E|È|É)?ME?\s*COUV/i.test(value)
    || (/SPONSOR/i.test(value) && /IMMO/i.test(value))
    || (/BANDEAU/i.test(value) && /IMMO/i.test(value))
    || /DOUBLE|DBLE/i.test(value)
    || (/\b1\s*PAGE\b/i.test(value) && !/1\s*\/\s*[23489]/i.test(value))
    || /1\s*\/\s*[234]/i.test(value)
  ) {
    return 'mag';
  }
  if (
    /SET/i.test(value)
    || (/AGENDA/i.test(value) && !/WEB|PAVE|NEWS/i.test(value))
    || (/CARRE/i.test(value) && !/WEB|PAVE|NEWS/i.test(value))
    || (/BANDEAU/i.test(value) && !/IMMO|HOROSCOPE|WEB|NEWS/i.test(value))
    || /DATE\s*ENCADREE/i.test(value)
  ) {
    return 'set';
  }
  if (/WEB/i.test(value) || /\bBAN\b/i.test(value) || /PAVE|MEGA/i.test(value)) return 'web';

  return 'unknown';
}

function kindToCampagneToken(kind) {
  if (kind === 'web' || kind === 'newsletter') return 'WEB';
  if (kind === 'reportage') return 'REP';
  if (kind === 'mag' || kind === 'set') return 'PRINT';
  return null;
}

function classifyForCampagneType(label) {
  const value = extractPlanDeComTarifLibelle(label);
  if (!value) return 'unknown';
  if (/REPORTAGE|FEUILLETON/i.test(value)) return 'reportage';
  if (/NEWS\s+FOOD|NEWS\s+SORTIES/i.test(value)) return 'web';
  if (/NEWSLETTER/i.test(value) || /WEB/i.test(value) || /\bBAN\b/i.test(value)) return 'web';
  return classifyPlanDeComSupport(value);
}

/**
 * Déduit MONO/DUO/TRIO + mix PRINT/WEB/REP depuis les supports (miroir API).
 */
export function resolvePlanDeComTypeCampagne(...formats) {
  const list = formats.length === 1 && Array.isArray(formats[0]) ? formats[0] : formats;
  let print = 0;
  let web = 0;
  let rep = 0;

  for (const format of list) {
    const label = extractPlanDeComTarifLibelle(format);
    if (!label) continue;
    const token = kindToCampagneToken(classifyForCampagneType(label));
    if (token === 'WEB') web += 1;
    else if (token === 'REP') rep += 1;
    else if (token === 'PRINT') print += 1;
  }

  const total = print + web + rep;
  if (total === 0) return null;

  if (total === 1) {
    if (web === 1) return 'MONO WEB';
    if (rep === 1) return 'MONO REP';
    return 'MONO PRINT';
  }

  if (total === 2) {
    if (rep === 1 && print === 1) return 'DUO REP/PRINT';
    if (rep === 1 && web === 1) return 'DUO REP/WEB';
    if (print === 2 || rep === 2) return 'DUO PRINT';
    if (web === 2) return 'DUO WEB';
    return 'DUO PRINT/WEB';
  }

  if (rep >= 1 && print >= 1 && web >= 1) return 'TRIO REP/PRINT/WEB';
  if (rep >= 1 && web >= 2) return 'TRIO REP/WEB/WEB';
  if (rep >= 1 && (print + rep) >= 3) return 'TRIO REP/PRINT/PRINT';
  if (print === 1 && web === 2) return 'TRIO PRINT/WEB/WEB';
  if (print === 2 && web === 1) return 'TRIO PRINT/PRINT/WEB';
  if (web === 3) return 'TRIO WEB';
  if (print === 3 || (print + rep) === 3) return 'TRIO PRINT';
  if (web >= 2) return rep > 0 ? 'TRIO REP/WEB/WEB' : 'TRIO PRINT/WEB/WEB';
  if (web === 1) return rep > 0 ? 'TRIO REP/PRINT/WEB' : 'TRIO PRINT/PRINT/WEB';
  return rep > 0 ? 'TRIO REP/PRINT/PRINT' : 'TRIO PRINT';
}

export function isPlanDeComSupportAllowed(label, _column = null, categorie = null) {
  const kind = classifyPlanDeComSupport(label, categorie);
  return PDC_PRINT_KINDS.includes(kind) || PDC_WEB_KINDS.includes(kind);
}

export function filterPlanDeComSupports(supports, { year, month }) {
  return (supports ?? [])
    .filter((support) => PDC_PRINT_KINDS.includes(support.kind) || PDC_WEB_KINDS.includes(support.kind))
    .filter((support) => !year || !month || isRefActiveInMonth(support, year, month));
}

function collectDisponibiliteSlots(disponibilites) {
  const seen = new Set();
  const slots = [];
  for (const slot of [...(disponibilites?.print ?? []), ...(disponibilites?.web ?? [])]) {
    const value = String(slot?.value ?? '');
    if (!value || seen.has(value)) continue;
    seen.add(value);
    slots.push(slot);
  }
  return slots;
}

function formatPlanDeComOptionHint(slotOrOption) {
  const client = String(slotOrOption?.client_label ?? '').trim();
  const commercial = String(slotOrOption?.commercial_nom ?? '').trim();
  if (client && commercial) {
    return `Déjà réservé pour ${client} · suivi par ${commercial}`;
  }
  if (client) {
    return `Déjà réservé pour ${client}`;
  }
  if (commercial) {
    return `Déjà réservé · suivi par ${commercial}`;
  }
  return 'Déjà réservé en option sur le planning';
}

/** Message toast à la sélection d’un support déjà en option. */
export function formatPlanDeComOptionToastMessage(slotOrOption) {
  const support = String(slotOrOption?.label ?? '').trim();
  const client = String(slotOrOption?.client_label ?? '').trim();
  const commercial = String(slotOrOption?.commercial_nom ?? '').trim();
  const head = support
    ? `${support} est déjà en option`
    : 'Ce support est déjà en option';
  const details = [];
  if (client) details.push(`Client : ${client}`);
  if (commercial) details.push(`Commercial : ${commercial}`);
  if (details.length) {
    return `${head} — ${details.join(' · ')}`;
  }
  return `${head} sur le planning`;
}

export { formatPlanDeComOptionHint };

function slotToOption(slot) {
  const isOption = Boolean(slot.is_option);
  const baseLabel = String(slot.label ?? '').replace(/\s*\(option\)\s*$/i, '').trim();
  const decoded = decodePlanDeComSupportValue(slot.value);
  const repeatable = Boolean(slot.repeatable) || isPlanDeComSupportRepeatable(slot.value, slot.slot_key || decoded.slotKey);
  return {
    value: slot.value,
    label: baseLabel,
    description: isOption ? formatPlanDeComOptionHint(slot) : null,
    badge: isOption ? 'En option' : null,
    kind: slot.kind || classifyPlanDeComSupport(slot.tarif_libelle || slot.label),
    disabled: false,
    is_option: isOption,
    client_label: slot.client_label || null,
    commercial_nom: slot.commercial_nom || null,
    repeatable,
    slotKey: slot.slot_key || decoded.slotKey,
    tarifLibelle: slot.tarif_libelle || decoded.tarifLibelle,
  };
}

export function buildPlanDeComSupportOptionsFromDisponibilites(disponibilites, { currentValue }) {
  // Les 3 colonnes Support 1/2/3 partagent le même vivier (print + web).
  const options = collectDisponibiliteSlots(disponibilites).map(slotToOption);

  const current = currentValue?.trim?.() ?? currentValue ?? '';
  if (current && !options.some((option) => option.value === current)) {
    const decoded = decodePlanDeComSupportValue(current);
    return [
      {
        value: current,
        label: decoded.label ? `${decoded.label} (indisponible)` : `${current} (indisponible)`,
        kind: classifyPlanDeComSupport(decoded.tarifLibelle || decoded.label),
        disabled: true,
      },
      ...options,
    ];
  }

  return options;
}

/**
 * Options regroupées pour les 4 points Mag / Set / Web / Newsletter.
 * @returns {Record<string, Array<{value: string, label: string, kind: string, disabled: boolean}>>}
 */
export function buildPlanDeComSupportOptionsByType(disponibilites, { excludeValues = [] } = {}) {
  const excluded = new Set((excludeValues ?? []).map((value) => String(value)));
  const all = collectDisponibiliteSlots(disponibilites).map(slotToOption);
  const grouped = Object.fromEntries(PDC_SUPPORT_TYPE_GROUPS.map((group) => [group.key, []]));
  const seenRepeatableTarif = new Set();

  for (const option of all) {
    const typeKey = planDeComSupportTypeKey(option.kind);
    if (!grouped[typeKey]) continue;

    const isSetSlot = String(option.slotKey || '').startsWith('set:');
    if (option.repeatable && !isSetSlot) {
      const tarif = option.tarifLibelle || extractPlanDeComTarifLibelle(option.value);
      const groupKey = `${typeKey}:${tarif}`;
      const isUnlimitedMag = /^mag:\d+:\d{2}:pub:/.test(String(option.slotKey || ''));
      if (isUnlimitedMag) {
        if (seenRepeatableTarif.has(groupKey)) continue;
        seenRepeatableTarif.add(groupKey);
        grouped[typeKey].push(option);
        continue;
      }
      if (excluded.has(String(option.value))) continue;
      if (seenRepeatableTarif.has(groupKey)) continue;
      seenRepeatableTarif.add(groupKey);
      grouped[typeKey].push(option);
      continue;
    }

    if (excluded.has(String(option.value))) continue;
    grouped[typeKey].push(option);
  }

  return grouped;
}

export function buildPlanDeComSupportOptions(supports, { column, year, month, currentValue }) {
  const available = filterPlanDeComSupports(supports, { column, year, month });
  const libelles = available.map((support) => support.libelle);
  const current = currentValue?.trim?.() ?? currentValue ?? '';

  if (current && !libelles.includes(extractPlanDeComTarifLibelle(current))) {
    const decoded = decodePlanDeComSupportValue(current);
    return [
      {
        value: current,
        label: decoded.label ? `${decoded.label} (indisponible)` : `${current} (indisponible)`,
        disabled: true,
      },
      ...libelles.map((libelle) => ({ value: libelle, label: libelle, disabled: false })),
    ];
  }

  return libelles.map((libelle) => ({ value: libelle, label: libelle, disabled: false }));
}

export function sanitizePlanDeComLigneSupports(ligne, supports, disponibilitesByKey = {}) {
  if (!ligne) return ligne;

  const year = ligne.annee;
  const month = planDeComMoisToNumber(ligne.mois);
  const cacheKey = year && month ? `${year}-${month}` : null;
  const disponibilites = cacheKey ? disponibilitesByKey[cacheKey] : null;

  const sanitize = (value, column) => {
    const trimmed = value?.trim?.() ?? value ?? '';
    if (!trimmed) return null;

    if (disponibilites) {
      const options = buildPlanDeComSupportOptionsFromDisponibilites(disponibilites, {
        column,
        currentValue: trimmed,
      });
      const allowedValues = options.filter((option) => !option.disabled).map((option) => option.value);
      if (allowedValues.includes(trimmed)) return trimmed;
      if (options.some((option) => option.value === trimmed && option.disabled)) return trimmed;
      return null;
    }

    const allowed = filterPlanDeComSupports(supports, { column, year, month });
    const tarif = extractPlanDeComTarifLibelle(trimmed);
    return allowed.some((support) => support.libelle === tarif) ? trimmed : null;
  };

  const items = [];
  const existing = Array.isArray(ligne.supports) ? ligne.supports : [];
  const maxLegacy = 3;
  const count = Math.max(existing.length, maxLegacy);
  for (let index = 0; index < count; index += 1) {
    const field = `format_${index + 1}`;
    const current = existing[index] ?? {
      value: ligne[field],
      frais: ligne[['frais_crea', 'frais_redac', 'frais_klub'][index]],
    };
    const value = sanitize(current?.value, field);
    if (!value) continue;
    items.push({
      value,
      frais: current?.frais ?? null,
    });
  }

  const next = { ...ligne, supports: items };
  ['format_1', 'format_2', 'format_3'].forEach((field, index) => {
    next[field] = items[index]?.value ?? null;
    next[['frais_crea', 'frais_redac', 'frais_klub'][index]] = items[index]?.frais ?? null;
  });
  return next;
}
